import {
  Box,
  Button,
  GridItem,
  HStack,
  Image,
  Skeleton,
  Text,
  Tooltip,
} from '@chakra-ui/react';
import {PANIER_GUTTER_LEFT} from './panier-layout';
import NiceModal from '@ebay/nice-modal-react';
import {useNavigate} from 'react-router-dom';
import {ModaleAlerteConfirmation} from './ModaleAlerteConfirmation';
import {ProductControlSideHeader} from '@/components/Product/ProductControlSideHeader';
import {PanierCommands} from 'ff-common/src/hooks/panier/use-panier-commands';
import {useTranslation} from 'react-i18next';
import {IconeTemperatureLivraison} from '@/components/IconeTemperatureLivraison';
import {routes, useLivraisonsPossiblesContext} from 'ff-common';
import {useClientRoute} from '@/hooks/auth/useClientRoute';
import {useHalState} from '@synako/haligator';
import {useClient} from '@/haligator-factory';
import type {CatalogueMarketplace, RapportMatch} from 'api-types/ffconnect';
import {VoirPdfButton} from '@/backoffice/domains/PdfOrderImport/VoirPdfButton';
import {FiltreMatching} from './panier-pdf-lignes';

// Panier PDF : Table des filtres. `largeur` est celle du placeholder affiché
// tant que les compteurs ne sont pas là.
const CHIPS_FILTRE_PANIER_PDF: {
  valeur: FiltreMatching;
  cle: string;
  couleur?: string;
  largeur: {base: string; xl: string};
}[] = [
  {
    valeur: 'tout',
    cle: 'panier.matching_filtre_tout',
    largeur: {base: '51px', xl: '51px'},
  },
  {
    valeur: 'ok',
    cle: 'panier.matching_filtre_ok',
    couleur: 'green.success',
    largeur: {base: '44px', xl: '74px'},
  },
  {
    valeur: 'alerte',
    cle: 'panier.matching_filtre_alerte',
    couleur: 'yellow.warning_text',
    largeur: {base: '44px', xl: '120px'},
  },
  {
    valeur: 'bloquant',
    cle: 'panier.matching_filtre_bloquant',
    couleur: 'red.fail',
    largeur: {base: '44px', xl: '180px'},
  },
];

export const PanierHeader: React.FC<{
  viderCommand: PanierCommands['vider'];
  nombre_de_lignes: number;
  type_panier?: CatalogueMarketplace;
  isViderDisabled?: boolean;
  isPanierPdf: boolean;
  chargementPdf?: boolean;
  pdfCheminFichier?: string;
  nbrLignesMatching?: RapportMatch['statistiques']['nb_lignes'];
  filtreMatching?: FiltreMatching;
  onFiltreMatchingChange?: (filtre: FiltreMatching) => void;
}> = ({
  viderCommand,
  nombre_de_lignes,
  type_panier,
  isViderDisabled,
  isPanierPdf,
  chargementPdf,
  pdfCheminFichier,
  nbrLignesMatching,
  filtreMatching,
  onFiltreMatchingChange,
}) => {
  const marketplaceId =
    type_panier !== undefined && type_panier !== 'FF' ? type_panier : undefined;
  const {t} = useTranslation();
  const navigate = useNavigate();
  const {buildClientRoute} = useClientRoute();
  const {effectiveTemperature, setSelectedMarketplaceId} =
    useLivraisonsPossiblesContext();
  const onPlusDeProduitClick = marketplaceId
    ? () =>
        navigate(
          buildClientRoute(
            routes.client.catalogue,
            {},
            {marketplace_id: marketplaceId},
          ),
        )
    : undefined;

  return (
    <GridItem
      display='flex'
      colSpan={{base: 1, lg: 6}}
      flexDir={{base: 'column', md: 'row'}}
      flexWrap='wrap'
      rowGap='2'
      pb={{base: '0', lg: '10px'}}
      {...PANIER_GUTTER_LEFT}
    >
      <HStack margin={{base: '2', lg: '0'}}>
        {/* Distinction entre paniers "normaux" et paniers issus d'un import PDF */}
        <Text fontWeight='600'>
          {t(isPanierPdf ? 'panier.panier_pdf' : 'panier.panier')}
        </Text>
        <Box
          textColor='gray.dark'
          fontWeight='600'
          fontSize='xs'
          bg='gray.light'
          p='1'
          borderRadius='8px'
          minW={'2em'}
          aspectRatio={1}
          textAlign='center'
        >
          {nombre_de_lignes}
        </Box>
        {marketplaceId && <MarketplaceLogo marketplaceId={marketplaceId} />}
        {effectiveTemperature !== 'les_deux' && (
          <Box>
            <HStack
              textColor='white'
              fontWeight='600'
              fontSize='xs'
              bg='gray.light'
              p='1'
              pr='3'
              borderRadius='30'
              backgroundColor={
                effectiveTemperature === 'negatif'
                  ? 'cyan.surgele'
                  : 'orange.warning'
              }
            >
              <IconeTemperatureLivraison
                temperature_tournee={effectiveTemperature}
                boxSize={6}
              />
              <Text>
                {effectiveTemperature === 'negatif'
                  ? t('temperature_livraison.livraison_surgelee')
                  : t('temperature_livraison.livraison_non_surgelee')}
              </Text>
            </HStack>
          </Box>
        )}
      </HStack>
      {/* Panier PDF : affichage des filtres */}
      {isPanierPdf && (
        <ChipsFiltreMatching
          nbrLignesMatching={nbrLignesMatching}
          chargement={chargementPdf ?? false}
          filtreMatching={filtreMatching}
          onFiltreMatchingChange={onFiltreMatchingChange}
        />
      )}

      <ProductControlSideHeader
        deleteText={t('panier.supprimer_panier_action')}
        deleteAction={() => {
          NiceModal.show(ModaleAlerteConfirmation, {
            validate_texte: t('panier.supprimer_panier_action'),
            body: t('panier.supprimer_panier_confirm'),
            validateAction: () => {
              viderCommand.submit({} as never);
              // Le panier marketplace supprimé n'existe plus => on repasse
              // sur FF, sinon navbar et side panel restent sur un
              // panier disparu.
              if (marketplaceId) {
                setSelectedMarketplaceId(null);
              }
            },
          });
        }}
        isDeleteDisabled={isViderDisabled}
        onPlusDeProduitClick={onPlusDeProduitClick}
      />
      {isPanierPdf && (
        <VoirPdfButton
          storagePath={pdfCheminFichier}
          h='35px'
          alignSelf='center'
          // Tant que le récapitulatif est empilé sous le panier, le bouton reste
          // dans l'en-tête. Dès qu'il passe en colonne de droite, c'est la page
          // panier qui rend le bouton au-dessus de lui.
          display={{base: 'inline-flex', lg: 'none'}}
          isDisabled={!pdfCheminFichier}
        />
      )}
    </GridItem>
  );
};

/**
 * Groupe de chips de filtre d'un panier PDF, rendu dans l'en-tête de la page
 * panier en mode commercial. Les chips quand les compteurs du matching sont connus, des placeholders de
 * même gabarit tant qu'on les attend, rien quand le processus n'en produit pas.
 */
const ChipsFiltreMatching: React.FC<{
  nbrLignesMatching?: RapportMatch['statistiques']['nb_lignes'];
  chargement: boolean;
  filtreMatching?: FiltreMatching;
  onFiltreMatchingChange?: (filtre: FiltreMatching) => void;
}> = ({
  nbrLignesMatching,
  chargement,
  filtreMatching,
  onFiltreMatchingChange,
}) => {
  const {t} = useTranslation();

  if (!nbrLignesMatching && !chargement) {
    return null;
  }

  return (
    <HStack
      margin={{base: '2', lg: '0'}}
      alignSelf='center'
      flexShrink={0}
      spacing={{base: '0.1rem', xl: '0'}}
    >
      {CHIPS_FILTRE_PANIER_PDF.map(({valeur, cle, couleur, largeur}) => {
        if (!nbrLignesMatching) {
          return (
            <Skeleton
              key={valeur}
              h='35px'
              w={largeur}
              ml={{base: 0, lg: '5px'}}
              borderRadius='8px'
            />
          );
        }
        const nombre =
          valeur === 'tout' ? undefined : nbrLignesMatching[valeur];
        return (
          <ChipFiltreMatching
            key={valeur}
            libelle={t(cle, {nombre})}
            nombre={nombre}
            couleurPastille={couleur}
            isActive={(filtreMatching ?? 'tout') === valeur}
            isDisabled={nombre === 0}
            onClick={() => {
              onFiltreMatchingChange?.(valeur);
            }}
          />
        );
      })}
    </HStack>
  );
};

// Panier PDF : Chip de filtre par statut de matching, dans le header du panier PDF.
// La pastille reprend les couleurs du badge
const ChipFiltreMatching: React.FC<{
  libelle: string;
  // Compteur seul, affiché à la place du libellé quand la place manque.
  nombre?: number;
  couleurPastille?: string;
  isActive: boolean;
  isDisabled: boolean;
  onClick: () => void;
}> = ({libelle, nombre, couleurPastille, isActive, isDisabled, onClick}) => {
  return (
    <Tooltip label={libelle}>
      <Button
        variant='outline'
        h='35px'
        px='8px'
        py='8px'
        ml={{base: 0, lg: '5px'}}
        borderRadius='8px'
        borderWidth='2px'
        fontSize='xs'
        fontWeight='500'
        bg={isActive ? 'blue.main_light_bg' : 'gray.75'}
        borderColor={isActive ? 'blue.border_dark' : 'gray.light'}
        color={isActive ? 'blue.main' : 'black.text'}
        _hover={{bg: isActive ? 'blue.main_light_bg' : 'gray.light'}}
        isDisabled={isDisabled}
        aria-pressed={isActive}
        aria-label={libelle}
        onClick={onClick}
        leftIcon={
          couleurPastille ? (
            <Box
              boxSize='8px'
              borderRadius='full'
              bg={couleurPastille}
            />
          ) : undefined
        }
      >
        {nombre === undefined ? (
          libelle
        ) : (
          <>
            {/* Le libellé cède la place au seul compteur quand l'en-tête est à l'étroit */}
            <Box
              as='span'
              display={{base: 'none', xl: 'inline'}}
            >
              {libelle}
            </Box>
            <Box
              as='span'
              display={{base: 'inline', xl: 'none'}}
            >
              {nombre}
            </Box>
          </>
        )}
      </Button>
    </Tooltip>
  );
};

const MarketplaceLogo: React.FC<{marketplaceId: CatalogueMarketplace}> = ({
  marketplaceId,
}) => {
  const {t} = useTranslation();
  const client = useClient();
  const {data: marketplaceData} = useHalState(
    client.follow('go:marketplace', {marketplace_id: marketplaceId}),
  );
  const logoSrc = marketplaceData?.logo_horizontal_couleur
    ? `${import.meta.env['VITE_MEDIA_URL']}${marketplaceData.logo_horizontal_couleur}`
    : undefined;

  if (logoSrc) {
    return (
      <Image
        src={logoSrc}
        alt={marketplaceData?.nom ?? marketplaceId}
        h='25px'
        objectFit='contain'
        borderRadius='md'
      />
    );
  }
  return (
    <Text
      color='black.text'
      whiteSpace='nowrap'
      fontSize='xs'
    >
      {t(`marketplace.${marketplaceId}`)}
    </Text>
  );
};
